/**
 * Per-node and graph-level validation for the Flow editor.
 * Marks unconfigured nodes with validated: false and errors for red outline display.
 */

import type { FlowNode, FlowEdge, NodeType } from '../types/flow-types';
import { analyzeFlowGraph } from './flowGraphSemantics';
import { validateFlowCard } from '../core/card-engine/registry';

/** Node types that must carry `data.sceneData` for scene JSON round-trip / runtime execution. */
export const RUNTIME_MAPPABLE_TYPES = new Set<NodeType>([
	'condition',
	'timeCondition',
	'manualCondition',
	'weatherCondition',
	'sunriseSunsetCondition',
	'energyCondition',
	'mathCondition',
	'sceneCondition',
	'alarmCondition',
	'scheduleCondition',
	'action',
	'notificationAction',
	'groupAction',
	'sceneAction',
	'alarmAction',
	'scriptAction',
	'setVariable',
	'presenceCondition',
	'virtualComponentEvent',
	'virtualComponentCondition',
	'virtualComponentAction',
]);

export const RUNTIME_MAPPING_ERROR = 'Not linked to the Flow runtime — open this card and reconfigure';

export function isRuntimeMappableType(type: string | undefined): boolean {
	return RUNTIME_MAPPABLE_TYPES.has(type as NodeType);
}

/** True only for metadata nodes that SceneToFlow could not match to scene semantics. */
export function hasPersistedOrphanData(node: FlowNode): boolean {
	const d = node.data;
	if (node.type === 'timeCondition' && d?.runtimeOrphan === true) {
		const subType = String(d.timeSubType || 'daily');
		if (subType === 'timer' && Number(d.interval) > 0) return false;
		if ((subType === 'd_cond' || subType === 'daily') && String(d.time || '').trim()) {
			return false;
		}
		if (
			(subType === 'ot_cond' || subType === 'execute_once') &&
			String(d.date || '').trim() &&
			String(d.executeTime || '').trim()
		) {
			return false;
		}
	}
	return Boolean(d && !d.sceneData && d.runtimeOrphan === true);
}

export function lacksRuntimeMapping(node: FlowNode): boolean {
	return isRuntimeMappableType(node.type) && !node.data?.sceneData;
}

export interface NodeValidationResult {
	isValid: boolean;
	errors: string[];
	warnings: string[];
}

export function validateNode(node: FlowNode, edges: FlowEdge[]): NodeValidationResult {
	const errors: string[] = [];
	const warnings: string[] = [];

	switch (node.type) {
		case 'note':
			break;

		case 'mathCondition':
			for (const issue of validateFlowCard(node)) errors.push(issue.message);
			break;

		case 'action':
			for (const issue of validateFlowCard(node)) errors.push(issue.message);
			break;

		case 'groupAction':
		case 'sceneAction':
		case 'alarmAction':
		case 'sceneCondition':
		case 'alarmCondition':
			for (const issue of validateFlowCard(node)) errors.push(issue.message);
			break;

		case 'condition':
			for (const issue of validateFlowCard(node)) errors.push(issue.message);
			// Warn when once-mode trigger has no explicit hold_off configured
			if (
				(node.data.executionMode === 'once' || !node.data.executionMode) &&
				node.data.hold_off == null &&
				!node.data.sceneData
			) {
				warnings.push('No hold-off set — will default to 5 min');
			}
			break;

		case 'trigger':
			if (node.data.triggerType === 'device' && !node.data.device?.id && !node.data.sceneData) {
				errors.push('Select a device');
			}
			break;

		case 'timeCondition':
		case 'scheduleCondition':
		case 'sunriseSunsetCondition':
		case 'weatherCondition':
		case 'energyCondition':
			for (const issue of validateFlowCard(node)) errors.push(issue.message);
			break;

		case 'setVariable':
			for (const issue of validateFlowCard(node)) errors.push(issue.message);
			break;

		case 'presenceCondition':
			for (const issue of validateFlowCard(node)) errors.push(issue.message);
			break;

		case 'timer':
			for (const issue of validateFlowCard(node)) errors.push(issue.message);
			break;

		case 'variableCondition':
		case 'virtualComponentEvent':
		case 'virtualComponentCondition':
		case 'virtualComponentAction':
			for (const issue of validateFlowCard(node)) errors.push(issue.message);
			break;

		case 'switchNode': {
			const deviceId = String(node.data.deviceId || '').trim();
			const sourceRef = String(node.data.sourceRef || '').trim();
			const rawProperty = node.data.property as
				| string
				| { id?: string; label?: string }
				| undefined;
			const property = String(
				typeof rawProperty === 'string'
					? rawProperty
					: rawProperty?.id || rawProperty?.label || '',
			).trim();

			// A legacy reconstructed switch can be serialized from sourceRef alone.
			// New editor switches need a device id so the runtime resource ref can be built.
			if (!deviceId && !sourceRef) {
				errors.push('Select a device');
			}
			if (!property && !sourceRef) {
				errors.push('Select a property');
			}
			break;
		}

		case 'variableSwitch':
			if (!String(node.data.variableName || '').trim()) errors.push('Variable name required');
			if (!Array.isArray(node.data.cases) || node.data.cases.length === 0)
				errors.push('Add at least one case');
			break;

		case 'hysteresisCondition': {
			if (!String(node.data.deviceId || '').trim() && !String(node.data.sourceRef || '').trim())
				errors.push('Select a device');
			if (!String(node.data.property || '').trim() && !String(node.data.sourceRef || '').trim())
				errors.push('Select a property');
			const lower = Number(node.data.lowerThreshold);
			const upper = Number(node.data.upperThreshold);
			if (!Number.isFinite(lower) || !Number.isFinite(upper)) errors.push('Set both thresholds');
			else if (lower >= upper) errors.push('OFF threshold must be lower than ON threshold');
			break;
		}

		case 'actionSequence': {
			const steps = Array.isArray(node.data.steps)
				? (node.data.steps as Array<Record<string, any>>)
				: [];
			if (!steps.length) errors.push('Add at least one sequence step');
			steps.forEach((step, index) => {
				if (step.type === 'wait' && Number(step.value) <= 0)
					errors.push(`Step ${index + 1}: set a delay`);
				if (step.type === 'setVariable' && !String(step.name || '').trim())
					errors.push(`Step ${index + 1}: variable name required`);
				if (
					step.type === 'notification' &&
					step.notificationType !== 'event_log' &&
					!String(step.message || '').trim()
				)
					errors.push(`Step ${index + 1}: message required`);
			});
			if (steps.at(-1)?.type === 'wait') {
				errors.push('The final sequence step cannot be a delay');
			}
			break;
		}

		case 'notificationAction': {
			for (const issue of validateFlowCard(node)) errors.push(issue.message);
			break;
		}

		case 'scriptAction': {
			for (const issue of validateFlowCard(node)) errors.push(issue.message);
			break;
		}
		// logicGate, manualCondition, etc. — no additional validation
	}

	if (lacksRuntimeMapping(node)) {
		if (hasPersistedOrphanData(node)) {
			errors.push(RUNTIME_MAPPING_ERROR);
		}
	}

	return { isValid: errors.length === 0, errors, warnings };
}

export function validateAllNodes(
	nodes: FlowNode[],
	edges: FlowEdge[],
): Map<string, NodeValidationResult> {
	return validateFlowWorkspace(nodes, edges).nodeResults;
}

export interface ProblematicNodeSummary {
	id: string;
	label: string;
	reason: string;
}

export function collectProblematicNodes(nodes: FlowNode[]): ProblematicNodeSummary[] {
	return nodes
		.filter((n) => !n.validated && (n.errors?.length ?? 0) > 0)
		.map((n) => ({
			id: n.id,
			label: String(
				(n.data as { displayLabel?: string; label?: string })?.displayLabel ||
					n.data?.label ||
					n.type ||
					n.id,
			),
			reason: n.errors[0] || 'Configuration error',
		}));
}

// ---- Graph-level structural validation ----

export interface GraphValidationIssue {
	severity: 'error' | 'warning';
	message: string;
	nodeId?: string;
}

export interface FlowValidationIssue extends GraphValidationIssue {
	source: 'node' | 'graph';
}

export interface FlowValidationReport {
	isValid: boolean;
	issues: FlowValidationIssue[];
	errors: FlowValidationIssue[];
	warnings: FlowValidationIssue[];
	nodeResults: Map<string, NodeValidationResult>;
}

/**
 * Canonical Flow-only validation report used by cards, save, run, import, and export.
 * Editor-only nodes and Flow-specific graph semantics remain valid inputs.
 */
export function validateFlowWorkspace(nodes: FlowNode[], edges: FlowEdge[]): FlowValidationReport {
	const nodeResults = new Map<string, NodeValidationResult>();
	const issues: FlowValidationIssue[] = [];
	const seen = new Set<string>();
	const addIssue = (issue: FlowValidationIssue) => {
		const key = `${issue.severity}:${issue.nodeId || ''}:${issue.message}`;
		if (seen.has(key)) return;
		seen.add(key);
		issues.push(issue);
	};

	for (const node of nodes) {
		const result = validateNode(node, edges);
		nodeResults.set(node.id, result);
		for (const message of result.errors) {
			addIssue({
				source: 'node',
				severity: 'error',
				nodeId: node.id,
				message,
			});
		}
		for (const message of result.warnings) {
			addIssue({
				source: 'node',
				severity: 'warning',
				nodeId: node.id,
				message,
			});
		}
	}

	for (const issue of validateGraph(nodes, edges)) {
		addIssue({ ...issue, source: 'graph' });
		if (issue.nodeId) {
			const result = nodeResults.get(issue.nodeId);
			if (result) {
				const target = issue.severity === 'error' ? result.errors : result.warnings;
				if (!target.includes(issue.message)) target.push(issue.message);
				result.isValid = result.errors.length === 0;
			}
		}
	}

	const errors = issues.filter((issue) => issue.severity === 'error');
	const warnings = issues.filter((issue) => issue.severity === 'warning');
	return {
		isValid: errors.length === 0,
		issues,
		errors,
		warnings,
		nodeResults,
	};
}

/**
 * Validate the overall graph structure beyond individual node rules.
 * Returns a list of issues (errors block saving; warnings are advisory).
 */
export function validateGraph(nodes: FlowNode[], edges: FlowEdge[]): GraphValidationIssue[] {
	const issues: GraphValidationIssue[] = [];
	const analysis = analyzeFlowGraph(nodes, edges);
	for (const cycle of analysis.cycles) {
		issues.push({
			severity: 'error',
			message: `Execution cycle detected across ${cycle.length} node${cycle.length === 1 ? '' : 's'} — remove the feedback connection`,
			nodeId: cycle[0],
		});
	}

	// Pre-build adjacency for O(1) lookups
	const adjOut = new Map<string, FlowEdge[]>();
	const adjIn = new Map<string, FlowEdge[]>();
	for (const edge of edges) {
		if (!adjOut.has(edge.source)) adjOut.set(edge.source, []);
		adjOut.get(edge.source)!.push(edge);
		if (!adjIn.has(edge.target)) adjIn.set(edge.target, []);
		adjIn.get(edge.target)!.push(edge);
	}

	for (const node of nodes) {
		if (node.type === 'note') continue;

		const outgoing = adjOut.get(node.id) ?? [];
		const incoming = adjIn.get(node.id) ?? [];

		if (node.type === 'logicGate') {
			const configuredInputs = Math.max(2, Number(node.data?.conditionCount || 2));
			if (incoming.length < configuredInputs) {
				issues.push({
					severity: 'error',
					message: `Logical gate "${node.data?.label || node.id}" has ${incoming.length}/${configuredInputs} connected inputs`,
					nodeId: node.id,
				});
			}
			if (outgoing.length === 0) {
				issues.push({
					severity: 'error',
					message: `Logical gate "${node.data?.label || node.id}" must have at least one output connected`,
					nodeId: node.id,
				});
			}
		}

		// Orphan detection: nodes with no edges at all (advisory only)
		if (outgoing.length === 0 && incoming.length === 0 && nodes.length > 1) {
			issues.push({
				severity: 'warning',
				message: `Node "${node.data?.label || node.type || node.id}" is disconnected`,
				nodeId: node.id,
			});
		}

		// NOR gate with no false-branch output: NOR inversion would be silently ignored
		if (node.type === 'logicGate' && String(node.data?.gateType) === 'nor') {
			const hasFalse = outgoing.some(
				(e) => e.sourceHandle === 'false' || e.sourceHandle?.includes('false'),
			);
			if (!hasFalse) {
				issues.push({
					severity: 'warning',
					message: `NOR gate "${node.data?.label || node.id}" has no FALSE output — the negated branch will be empty`,
					nodeId: node.id,
				});
			}
		}
	}

	return issues;
}
